import { IconDownload, IconEye } from "@tabler/icons-react";
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { formatFileUrl, API_URL } from "../../utils/api";

export default function CategoryBooks() {
    const { categoryId } = useParams();
    const navigate = useNavigate();

    const catIdLower = categoryId?.toLowerCase();
    const titles = { 'english': 'English Books', 'malayalam': 'Malayalam Books', 'arabic': 'Arabic Books' };

    const [books, setBooks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [categoryTitle, setCategoryTitle] = useState(titles[catIdLower] || categoryId);

    useEffect(() => {
        window.scrollTo(0, 0);

        const fetchData = async () => {
            try {
                const catRes = await fetch(`${API_URL}/library/categories?type=book`);
                if (!catRes.ok) throw new Error("API not ok");
                const categories = await catRes.json();
                const matchedCat = categories.find(c => c.link === categoryId || c._id === categoryId);

                if (matchedCat) {
                    setCategoryTitle(matchedCat.title);
                    const itemRes = await fetch(`${API_URL}/library/items?category=${matchedCat._id}`);
                    if (!itemRes.ok) throw new Error("API not ok");
                    const items = await itemRes.json();
                    setBooks(items);
                }
            } catch (err) {
                console.warn("Backend unavailable:", err);
                setBooks([]);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [categoryId]);

    const handleOpenPDF = (pdfUrl, title) => {
        if (pdfUrl) {
            const fullUrl = formatFileUrl(pdfUrl);
            window.open(`/view-pdf?url=${encodeURIComponent(fullUrl)}&title=${encodeURIComponent(title)}`, "_blank");
        }
    };

    const handleDownloadPDF = async (e, pdfUrl, title) => {
        e.stopPropagation();
        if (pdfUrl) {
            const fullUrl = formatFileUrl(pdfUrl);
            try {
                const response = await fetch(fullUrl);
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = `${title}.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } catch (error) {
                console.error("Download failed", error);
                const link = document.createElement("a");
                link.href = fullUrl;
                link.download = `${title}.pdf`;
                link.click();
            }
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 pt-[80px] pb-10 px-4 sm:px-6 lg:px-8 font-libre">
            <div className="max-w-7xl mx-auto">
                {/* Header Section */}
                <div className="mb-8 border-b border-gray-200 pb-6">
                    <button
                        onClick={() => navigate('/library')}
                        className="flex items-center text-gray-600 hover:text-pumpkinToast-600 transition-colors mb-4 group"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1 transform group-hover:-translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        Back to Library
                    </button>
                    <h1 className="text-3xl md:text-4xl font-bold text-pumpkinToast-600 font-AndoraModernSerif">
                        {categoryTitle}
                    </h1>
                </div>

                {/* Books Grid */}
                {loading ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-5">
                        {Array(12).fill(0).map((_, i) => (
                            <div key={i} className="group relative bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                                <div className="relative aspect-[2/3] bg-gray-200 animate-pulse"></div>
                                <div className="p-2 sm:p-2.5 bg-white flex flex-col items-center">
                                    <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse mb-1"></div>
                                    <div className="h-3 bg-gray-200 rounded w-1/2 animate-pulse"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : books.length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-5">
                        {books.filter(Boolean).map((item) => (
                            <div
                                key={item._id || item.id}
                                onClick={() => item.pdf && handleOpenPDF(item.pdf, item.title)}
                                className="group relative bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer flex flex-col"
                            >
                                <div className="relative aspect-[2/3] overflow-hidden bg-gray-200">
                                    <img
                                        src={formatFileUrl(item.coverImage || item.bg)}
                                        alt={item.title || "Book Cover"}
                                        className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                                        onError={(e) => { e.currentTarget.src = "https://tipyaanacademy.com/wp-content/uploads/2023/07/muslim-school.jpg"; }}
                                    />
                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                    <div className="absolute inset-0 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (item.pdf) handleOpenPDF(item.pdf, item.title);
                                            }}
                                            className="bg-white/90 text-gray-800 p-2 rounded-full hover:bg-white hover:text-pumpkinToast-600 shadow-lg transition-colors"
                                            title="View"
                                        >
                                            <IconEye size={18} />
                                        </button>
                                        <button
                                            onClick={(e) => item.pdf && handleDownloadPDF(e, item.pdf, item.title)}
                                            className="bg-white/90 text-gray-800 p-2 rounded-full hover:bg-white hover:text-pumpkinToast-600 shadow-lg transition-colors"
                                            title="Download"
                                        >
                                            <IconDownload size={18} />
                                        </button>
                                    </div>
                                </div>
                                <div className="p-2 sm:p-2.5 bg-white flex flex-col items-center">
                                    <h3 className="font-bold text-xs sm:text-sm text-gray-800 group-hover:text-pumpkinToast-600 transition-colors line-clamp-2 leading-tight text-center">
                                        {item.title || "Untitled Book"}
                                    </h3>
                                    {item.month && (
                                        <p className="text-gray-400 text-[9px] sm:text-[10px] mt-1 font-bold uppercase tracking-widest text-center">
                                            {item.month}
                                        </p>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="p-20 text-center text-gray-500 italic bg-white rounded-xl shadow-sm border border-gray-100">
                        No books found in this category.
                    </div>
                )}
            </div>
        </div>
    );
}
